import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Calendar, MapPin, Building2, Filter, ChevronLeft, ChevronRight, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrganizersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [organizers, setOrganizers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ countries: [], regions: [] });
  const [showFilters, setShowFilters] = useState(false);

  const search = searchParams.get("search") || "";
  const country = searchParams.get("country") || "";
  const region = searchParams.get("region") || "";
  const page = parseInt(searchParams.get("page") || "0");
  const perPage = 24;

  const fetchOrganizers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * perPage, limit: perPage };
      if (search) params.search = search;
      if (country) params.country = country;
      if (region) params.region = region;

      const res = await axios.get(`${API}/organizers`, { params });
      setOrganizers(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, country, region, page]);

  useEffect(() => { fetchOrganizers(); }, [fetchOrganizers]);
  useEffect(() => { axios.get(`${API}/organizers/filters`).then(r => setFilters(r.data)).catch(() => {}); }, []);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.delete("page");
    setSearchParams(p);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <Calendar className="h-6 w-6 text-blue-500" />
            <span className="text-xl font-bold">ExpoPulse <span className="text-blue-500">Events</span></span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/exhibitions")} className="text-gray-600 hover:text-gray-900 transition text-sm">Exhibitions</button>
            <button onClick={() => navigate("/organizers")} className="text-gray-900 font-medium text-sm">Organizers</button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Trade Show Organizers</h1>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 flex bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center pl-4"><Search className="h-5 w-5 text-gray-500" /></div>
            <input
              type="text" placeholder="Search organizers..."
              defaultValue={search}
              onKeyDown={(e) => e.key === "Enter" && updateParam("search", e.target.value)}
              className="flex-1 bg-transparent px-4 py-3 text-gray-900 placeholder-slate-500 outline-none"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-slate-600 transition">
            <Filter className="h-4 w-4" /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-4 mb-8 bg-white border border-gray-200 rounded-lg p-4">
            <select value={country} onChange={(e) => updateParam("country", e.target.value)}
              className="bg-gray-100 border border-slate-600 rounded px-3 py-2 text-sm">
              <option value="">All Countries</option>
              {filters.countries?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={region} onChange={(e) => updateParam("region", e.target.value)}
              className="bg-gray-100 border border-slate-600 rounded px-3 py-2 text-sm">
              <option value="">All Regions</option>
              {filters.regions?.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}

        <div className="text-sm text-gray-500 mb-6">{total.toLocaleString()} organizers found</div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {organizers.map((org) => (
              <div key={org._id} onClick={() => navigate(`/organizer/${org._id}`)}
                className="bg-white border border-gray-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-sm transition cursor-pointer group">
                <div className="flex items-start gap-4">
                  {org.logo_url && (
                    <img src={org.logo_url} alt="" className="w-14 h-14 rounded-lg object-cover bg-white shrink-0" onError={(e) => e.target.style.display='none'} />
                  )}
                  <div className="min-w-0">
                    <h3 className="font-semibold group-hover:text-blue-600 transition truncate">{org.name}</h3>
                    {org.category && <div className="text-xs text-blue-600 mt-1">{org.category}</div>}
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-2">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{[org.city, org.country].filter(Boolean).join(", ")}</span>
                    </div>
                    {org.region && (
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                        <Globe className="h-3 w-3 shrink-0" /> {org.region}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-10">
            <button disabled={page === 0}
              onClick={() => { const p = new URLSearchParams(searchParams); p.set("page", page - 1); setSearchParams(p); }}
              className="flex items-center gap-1 bg-white border border-gray-200 rounded px-4 py-2 disabled:opacity-30 hover:border-slate-600 transition">
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <span className="text-sm text-gray-500">Page {page + 1} of {totalPages}</span>
            <button disabled={page >= totalPages - 1}
              onClick={() => { const p = new URLSearchParams(searchParams); p.set("page", page + 1); setSearchParams(p); }}
              className="flex items-center gap-1 bg-white border border-gray-200 rounded px-4 py-2 disabled:opacity-30 hover:border-slate-600 transition">
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
